import API from "../../services/api";

const STATUSES = ["present", "absent", "leave", "half-day", "week-off", "holiday"];

/** Status codes seen in API/DB beyond the primary mapping (for reporting). */
export const UNMAPPED_STATUS_LOG = new Set();

const STATUS_TO_DASHBOARD = {
  P: "present",
  present: "present",
  A: "absent",
  absent: "absent",
  HD: "half-day",
  half_day: "half-day",
  "half-day": "half-day",
  L: "leave",
  leave: "leave",
  paid_leave: "leave",
  PL: "leave",
  unpaid_leave: "leave",
  UL: "leave",
  H: "holiday",
  holiday: "holiday",
  WO: "week-off",
  wo: "week-off",
  week_off: "week-off",
  WFH: "present",
  wfh: "present",
  OD: "present",
  on_duty: "present",
};

function pad(n) {
  return String(n).padStart(2, "0");
}

export function formatDateISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Monday of the week containing `refDate`, shifted by `weekOffset` weeks. */
export function getWeekStart(refDate = new Date(), weekOffset = 0) {
  const d = new Date(refDate);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff + weekOffset * 7);
  return d;
}

export function getWeekDates(weekStartDate) {
  const start =
    weekStartDate instanceof Date
      ? new Date(weekStartDate)
      : new Date(`${weekStartDate}T00:00:00`);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function weekRangeISO(weekStartDate) {
  const dates = getWeekDates(weekStartDate);
  return {
    from_date: formatDateISO(dates[0]),
    to_date: formatDateISO(dates[6]),
  };
}

export function normalizeStatusToDashboard(status) {
  if (status == null || status === "") return "absent";
  const raw = String(status).trim();
  const key = raw in STATUS_TO_DASHBOARD ? raw : raw.toLowerCase();
  if (STATUS_TO_DASHBOARD[key]) return STATUS_TO_DASHBOARD[key];
  UNMAPPED_STATUS_LOG.add(raw);
  console.warn("[attendanceService] Unmapped attendance status:", raw);
  return "absent";
}

export function extractHHMM(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const iso = value.match(/T(\d{2}):(\d{2})/);
    if (iso) return `${iso[1]}:${iso[2]}`;
    const timeOnly = value.match(/^(\d{1,2}):(\d{2})/);
    if (timeOnly) {
      return `${pad(Number(timeOnly[1]))}:${timeOnly[2]}`;
    }
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return null;
}

function formatWorkDate(workDate) {
  if (!workDate) return null;
  if (typeof workDate === "string") return workDate.slice(0, 10);
  return formatDateISO(new Date(workDate));
}

export function mapAttendanceRow(row) {
  const employeeId = String(row.employee_id ?? row.employeeId ?? "");
  const date = formatWorkDate(row.work_date ?? row.date);
  return {
    attendanceId: row.attendance_id ?? row.id ?? null,
    employeeId,
    date,
    status: normalizeStatusToDashboard(row.status),
    checkIn: extractHHMM(row.time_in ?? row.checkIn),
    checkOut: extractHHMM(row.time_out ?? row.checkOut),
    hoursWorked: Number(row.work_hours ?? row.hoursWorked ?? 0) || 0,
  };
}

function mapEmployeeRow(emp) {
  const employeeId = String(emp.employee_id ?? emp.employeeId ?? "");
  const name =
    emp.display_name ||
    [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
    emp.employee_code ||
    "Employee";
  return {
    employeeId,
    employeeCode: emp.employee_code ?? "",
    name,
    department: emp.department?.name ?? emp.department_name ?? "",
    designation: emp.designation?.name ?? emp.designation_name ?? "",
    shift: emp.shift ?? "09:00 – 18:00",
  };
}

async function fetchActiveEmployeesFromApi() {
  const { data } = await API.get("/employees/", {
    params: { active_only: true },
  });
  const list = Array.isArray(data) ? data : data?.items ?? [];
  return list.map(mapEmployeeRow);
}

async function fetchAttendanceRows({ from_date, to_date, employee_id }) {
  const params = { from_date, to_date };
  if (employee_id) params.employee_id = employee_id;
  const { data } = await API.get("/attendance/", { params });
  const list = Array.isArray(data) ? data : data?.items ?? data?.records ?? [];
  return list.map(mapAttendanceRow);
}

function emptyDayRecord(employeeId, date) {
  return {
    employeeId,
    date,
    status: "absent",
    checkIn: null,
    checkOut: null,
    hoursWorked: 0,
  };
}

function mergeWeekRecords(employeeId, weekStartDate, apiRows) {
  const byDate = new Map(
    apiRows
      .filter((r) => String(r.employeeId) === String(employeeId))
      .map((r) => [r.date, r])
  );
  return getWeekDates(weekStartDate).map((d) => {
    const date = formatDateISO(d);
    return byDate.get(date) ?? emptyDayRecord(employeeId, date);
  });
}

export async function getWeekAttendance(employeeId, weekStartDate) {
  const { from_date, to_date } = weekRangeISO(weekStartDate);

  try {
    const rows = await fetchAttendanceRows({
      from_date,
      to_date,
      employee_id: employeeId,
    });
    return mergeWeekRecords(employeeId, weekStartDate, rows);
  } catch (err) {
    console.error("[attendanceService] getWeekAttendance failed:", err);
    return mergeWeekRecords(employeeId, weekStartDate, []);
  }
}

export async function getAllEmployeesWeekAttendance(weekStartDate) {
  const { from_date, to_date } = weekRangeISO(weekStartDate);

  try {
    const [employees, attendanceRows] = await Promise.all([
      fetchActiveEmployeesFromApi(),
      fetchAttendanceRows({ from_date, to_date }),
    ]);

    if (!employees.length) {
      return [];
    }

    return employees.map((emp) => ({
      ...emp,
      records: mergeWeekRecords(emp.employeeId, weekStartDate, attendanceRows),
    }));
  } catch (err) {
    console.error("[attendanceService] getAllEmployeesWeekAttendance failed:", err);
    return [];
  }
}

export async function markCheckIn(employeeId, date, time) {
  const { data } = await API.post("/attendance/", {
    employee_id: employeeId,
    work_date: date,
    time_in: time,
  });
  return mapAttendanceRow(data);
}

export async function markCheckOut(employeeId, date, time) {
  const { from_date, to_date } = { from_date: date, to_date: date };
  const { data } = await API.get("/attendance/", {
    params: { from_date, to_date, employee_id: employeeId },
  });
  const list = Array.isArray(data) ? data : data?.items ?? [];
  const row = list[0];
  if (row?.attendance_id) {
    const { data: updated } = await API.put(`/attendance/${row.attendance_id}`, {
      time_out: time,
    });
    return mapAttendanceRow(updated);
  }
  const { data: created } = await API.post("/attendance/", {
    employee_id: employeeId,
    work_date: date,
    time_out: time,
  });
  return mapAttendanceRow(created);
}
